import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { useFormik } from "formik";
import MUIDataTable from "mui-datatables";
import { Button, Modal, Form, Card, Row, Col } from "react-bootstrap";
import * as alert from "../../../redux/alertSnackBarRedux";
import * as confirmModal from "../../../redux/confirmModalRedux";
import * as yearAllocationSettingService from "../../../services/management/yearAllocationSettingService";
import { getInputClasses } from "../../../helpers/helper";
import * as Yup from "yup";

const initialSetting = {
    id: 0,
    year: new Date().getFullYear(),
    overheadPercent: 0.15,
    maxAdminCapPercent: 0.15,
    defaultAdminAllocationPercent: 0.18,
    isLocked: false
};

const YearAllocationSettingsPage = (props) => {
    const { showAlert, showConfirmModal, hideConfirmModal, auth } = props;
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSetting, setSelectedSetting] = useState(initialSetting);

    // Access restricted to admin and Chip only
    const hasAccess = auth && auth.user && (auth.user.username === 'admin' || auth.user.username === 'Chip');

    useEffect(() => {
        if (hasAccess) {
            loadTableData();
        }
    }, [hasAccess]);

    const loadTableData = () => {
        yearAllocationSettingService.getAll().then(res => {
            const result = res.data || res;
            setData(result || []);
        }).catch((ex) => {
            showAlert({ message: ex.message || "Failed to load settings", variant: "error" });
        });
    };

    const editSetting = (id) => {
        yearAllocationSettingService.getById(id).then(res => {
            const result = res.data || res;
            setSelectedSetting(result);
            setIsModalOpen(true);
        }).catch((ex) => {
            showAlert({ message: ex.message || "Failed to load setting", variant: "error" });
        });
    };

    const deleteSetting = (id) => {
        yearAllocationSettingService.deleteSetting(id).then(res => {
            showAlert({ message: "Setting deleted successfully", variant: "success" });
            loadTableData();
            hideConfirmModal();
        }).catch((ex) => {
            showAlert({ message: ex.message || "Failed to delete setting", variant: "error" });
            hideConfirmModal();
        });
    };

    const openNewSettingModal = () => {
        setSelectedSetting({
            ...initialSetting,
            year: new Date().getFullYear() + 1 // Default to next year
        });
        setIsModalOpen(true);
    };

    const validationSchema = Yup.object().shape({
        year: Yup.number()
            .required("Year is required")
            .min(2020, "Year must be 2020 or later")
            .max(2100, "Year must be before 2100"),
        overheadPercent: Yup.number()
            .required("Overhead % is required")
            .min(0, "Must be 0 or greater")
            .max(1, "Must be 1 or less (100%)"),
        maxAdminCapPercent: Yup.number()
            .required("Max Admin Cap % is required")
            .min(0, "Must be 0 or greater")
            .max(1, "Must be 1 or less (100%)"),
        defaultAdminAllocationPercent: Yup.number()
            .required("Default Admin Allocation % is required")
            .min(0, "Must be 0 or greater")
            .max(1, "Must be 1 or less (100%)")
    });

    const formik = useFormik({
        initialValues: selectedSetting,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            yearAllocationSettingService.save(values).then(res => {
                showAlert({ message: "Setting saved successfully", variant: "success" });
                setIsModalOpen(false);
                loadTableData();
            }).catch((ex) => {
                showAlert({ message: ex.response?.data?.message || ex.message || "Failed to save setting", variant: "error" });
            });
        }
    });

    const formatPercent = (value) => {
        return (value * 100).toFixed(1) + '%';
    };

    const columns = [
        {
            name: "id",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                setCellProps: () => ({ style: { width: "20%" } }),
                customBodyRenderLite: (dataIndex) => {
                    const setting = data[dataIndex];
                    return (
                        <>
                            <button
                                className="btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5"
                                onClick={() => editSetting(setting.id)}
                                disabled={setting.isLocked}
                            >
                                {setting.isLocked ? 'Locked' : 'Edit'}
                            </button> &nbsp;
                            {!setting.isLocked && (
                                <button
                                    onClick={() => showConfirmModal({
                                        title: "Delete Year Setting",
                                        text: `Are you sure you want to delete settings for year ${setting.year}?`,
                                        rightBtnText: "Confirm",
                                        btnAction: () => deleteSetting(setting.id)
                                    })}
                                    className="btn btn-sm btn-danger font-weight-bold py-2 px-3 px-xxl-5 my-1"
                                >
                                    Delete
                                </button>
                            )}
                        </>
                    );
                }
            }
        },
        {
            name: "year",
            label: "Year",
            options: {
                filter: true,
                sort: true
            }
        },
        {
            name: "overheadPercent",
            label: "Overhead %",
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => formatPercent(value)
            }
        },
        {
            name: "maxAdminCapPercent",
            label: "Max Admin Cap %",
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => formatPercent(value)
            }
        },
        {
            name: "defaultAdminAllocationPercent",
            label: "Default Admin Allocation %",
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => formatPercent(value)
            }
        },
        {
            name: "isLocked",
            label: "Status",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => (
                    <span className={`label label-lg label-light-${value ? 'danger' : 'success'} label-inline`}>
                        {value ? 'Locked' : 'Active'}
                    </span>
                )
            }
        }
    ];

    const options = {
        filterType: 'dropdown',
        responsive: 'standard',
        selectableRows: 'none',
        download: false,
        print: false,
        viewColumns: false
    };

    if (!hasAccess) {
        return (
            <Card className="mb-5">
                <Card.Body>
                    <h4>Access Denied</h4>
                    <p>You do not have permission to view this page.</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-5">
                <Card.Header>
                    <h4 className="mb-0">Year Allocation Settings</h4>
                    <p className="text-muted mb-0 mt-2">
                        Configure cost allocation percentages for each fiscal year. Lock a year to preserve historical data.
                    </p>
                </Card.Header>
                <Card.Body>
                    <div className="mb-4">
                        <Button variant="primary" onClick={openNewSettingModal}>
                            Add Year Settings
                        </Button>
                    </div>

                    <MUIDataTable
                        title="Year Allocation Settings"
                        data={data}
                        columns={columns}
                        options={options}
                    />
                </Card.Body>
            </Card>

            <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedSetting.id > 0 ? `Edit Year ${selectedSetting.year} Settings` : 'Add Year Settings'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Year *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="year"
                                        disabled={selectedSetting.id > 0}
                                        className={getInputClasses(formik, "year")}
                                        {...formik.getFieldProps("year")}
                                    />
                                    {formik.touched.year && formik.errors.year && (
                                        <div className="invalid-feedback d-block">{formik.errors.year}</div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="isLocked"
                                        label="Lock this year (prevents future changes)"
                                        name="isLocked"
                                        checked={formik.values.isLocked || false}
                                        onChange={(e) => formik.setFieldValue('isLocked', e.target.checked)}
                                        className="mt-4"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />
                        <h6 className="mb-3">Allocation Percentages</h6>
                        <p className="text-muted small mb-3">
                            Enter values as decimals (e.g., 0.15 = 15%, 0.18 = 18%)
                        </p>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>QuickBooks Overhead % *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="overheadPercent"
                                        placeholder="0.15"
                                        className={getInputClasses(formik, "overheadPercent")}
                                        {...formik.getFieldProps("overheadPercent")}
                                    />
                                    <Form.Text className="text-muted">
                                        Applied to non-labor costs for non-admin jobs
                                    </Form.Text>
                                    {formik.touched.overheadPercent && formik.errors.overheadPercent && (
                                        <div className="invalid-feedback d-block">{formik.errors.overheadPercent}</div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Max Admin Cap % *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="maxAdminCapPercent"
                                        placeholder="0.15"
                                        className={getInputClasses(formik, "maxAdminCapPercent")}
                                        {...formik.getFieldProps("maxAdminCapPercent")}
                                    />
                                    <Form.Text className="text-muted">
                                        Maximum admin cost as % of job revenue
                                    </Form.Text>
                                    {formik.touched.maxAdminCapPercent && formik.errors.maxAdminCapPercent && (
                                        <div className="invalid-feedback d-block">{formik.errors.maxAdminCapPercent}</div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Default Admin Allocation % *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="defaultAdminAllocationPercent"
                                        placeholder="0.18"
                                        className={getInputClasses(formik, "defaultAdminAllocationPercent")}
                                        {...formik.getFieldProps("defaultAdminAllocationPercent")}
                                    />
                                    <Form.Text className="text-muted">
                                        Default allocation for jobs without custom %
                                    </Form.Text>
                                    {formik.touched.defaultAdminAllocationPercent && formik.errors.defaultAdminAllocationPercent && (
                                        <div className="invalid-feedback d-block">{formik.errors.defaultAdminAllocationPercent}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="alert alert-info mt-3">
                            <strong>Note:</strong> These settings affect financial calculations for all jobs approved in this year.
                            Individual jobs can have custom admin allocation percentages set on their detail pages.
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={formik.handleSubmit}>
                        Save Settings
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, { ...alert.actions, ...confirmModal.actions })(YearAllocationSettingsPage);
